import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import {
  ComcastErrorType,
  platformStoreDelete,
  triggerSentryError,
} from '../../../../shared/helpers'
import { checkDeviceId } from '../../actions/contentActions'
import qrCodeImg from '../../assets/img/pbsActivateURL.png'
import FocusableButtonTS from '../../components/FocusableButton/FocusableButtonTS'
import QRCodeGenerator from '../../components/QRCodeGenerator'
import Spinner from '../../components/Spinner'
import { useAppSelector } from '../../hooks/useStore'
import tts from '../../lib/TTS'
import { authCodeRequest } from '../../lib/utils'
import { useActivationCode } from './useActivationCode'
import {
  DEVICE_KEY,
  EACH_FIFTEEN_SECONDS,
  EACH_FIVE_SECONDS,
  getActivationRedirectLink,
  setTimer,
  TWELVE_ATTEMPTS,
  TWO_HUNDRED_FORTY_ATTEMPTS,
} from './utils'

// TODO: remove this when whole app is migrated to react spatial navigation
declare global {
  interface Window {
    focusedComponent: {
      handleBySpatialNavigation?: boolean
    }
  }
}

function ActivationCodeScreen() {
  const ACTIVATION_BASE_URL = 'https://www.pbs.org/tv'

  const navigate = useNavigate()
  const location = useLocation()
  const [authCode, setAuthCode] = useState<string>('')
  const [activationUrl, setActivationUrl] = useState<string>('')
  const deviceId = useAppSelector((state) => state.device.deviceId)
  const app = deviceId.split('-')[1]

  const { timerResets, pollActivation, navigateHome } = useActivationCode()

  const finishRetries = () => {
    // polling completed, error code 500005
    // expire code,return user to activation screen
    platformStoreDelete(DEVICE_KEY)
    checkDeviceId()
    // return user to activation screen
    navigate(getActivationRedirectLink(location.search))
  }

  const newCodeHandler = () => {
    tts.pushTextToRead('Selected Get New Code Button')
    platformStoreDelete(DEVICE_KEY)
    checkDeviceId()
  }

  const lowerPriorityActivationRetries = () =>
    // eslint-disable-next-line implicit-arrow-linebreak
    timerResets.current.push(
      setTimer(
        pollActivation,
        EACH_FIFTEEN_SECONDS,
        TWO_HUNDRED_FORTY_ATTEMPTS,
        finishRetries
      )
    )

  // Workaround to allow new focus components to work with spatial navigation and old components
  // work with focusable HoC
  // TODO: remove this when whole app is migrated to react spatial navigation
  useEffect(() => {
    window.focusedComponent = {}
    window.focusedComponent.handleBySpatialNavigation = true
    return () => {
      window.focusedComponent.handleBySpatialNavigation = false
    }
  }, [])

  useEffect(() => {
    navigateHome()
    timerResets.current.push(
      setTimer(
        pollActivation,
        EACH_FIVE_SECONDS,
        TWELVE_ATTEMPTS,
        lowerPriorityActivationRetries
      )
    )

    return () => {
      tts.stopRead()
    }
  }, [])

  useEffect(() => {
    if (deviceId && app) {
      authCodeRequest(deviceId, app)
        .then((resp) => {
          setAuthCode(resp.authCode)
          setActivationUrl(`${ACTIVATION_BASE_URL}?authCode=${resp.authCode}`)
        })
        .catch((err) => {
          triggerSentryError('authCodeRequest', 'ActivationCodeScreen.js', {
            errorMessage: err.message ?? 'Undefined error message (default)',
            deviceId,
            app,
            errorLocation: 'ActivationCodeScreen - Generating Code',
            errorType: ComcastErrorType.restriction,
          })
        })
    }
  }, [deviceId, app])

  useEffect(() => {
    if (authCode) {
      tts.stopRead()
      tts.addTextToRead(`To sign in this device. Step 1. From a mobile device or computer, visit: pbs.org/tv.
      or point your phone or tablet camera at the QR code below.
      Step 2. Enter code: ${authCode
        .split('')
        .join(
          ' , '
        )}. Once you sign in, this screen will automatically update within 30 seconds. The code again is: ${authCode
        .split('')
        .join(
          ' , '
        )}. If you have any issues activating with the code listed above, please use the button below to get a new code ..... Focused on Get New Code Button
      }`)
    }
    return () => tts.stopRead()
  }, [authCode])

  return (
    <div className="activation-code-screen">
      <h1 className="activation-code-screen__header">SIGN IN</h1>
      <div className="activation-code-screen__body">
        <div className="activation-code-screen__step1">
          <h2 className="activation-code-screen__step-title">Step 1</h2>
          <div className="activation-code-screen__step1-wrapper">
            <div className="activation-code-screen__step1-content">
              <p>From a mobile device or computer, visit</p>
              <p className="activation-code-screen__url">pbs.org/tv</p>
              <p className="activation-code-screen__or-separator">OR</p>
              <p className="activation-code-screen__qr-info">
                point your phone or tablet camera at the QR code below
              </p>
              {authCode ? (
                <div className="activation-code-screen__qr-code">
                  <QRCodeGenerator
                    key={authCode}
                    text={activationUrl}
                    width={300}
                    fgColor="#2035C5"
                    fallbackContent={
                      <img
                        alt="no-alt"
                        className="activation-code-screen__qr-code"
                        src={qrCodeImg}
                      />
                    }
                  />
                </div>
              ) : (
                <Spinner className="activation-code-screen__spinner" />
              )}
            </div>
          </div>
        </div>
        <div className="activation-code-screen__step2">
          <h2 className="activation-code-screen__step-title activation-code-screen__step2-title">
            Step 2
          </h2>
          <div className="activation-code-screen__step2-content">
            <p>Enter code:</p>
            <p className="activation-code-screen__auth-code">{authCode}</p>
            <p className="activation-code-screen__sign-in-info">
              Once you sign in, this screen will automatically update within 30
              seconds.
            </p>
            <div className="activation-code-screen__new-code">
              <p className="activation-code-screen__new-code-info">
                Code not working?
              </p>
              <FocusableButtonTS
                className="activation-code-screen__new-code-button"
                stealFocus
                onEnterPress={newCodeHandler}
              >
                Get New Code
              </FocusableButtonTS>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ActivationCodeScreen
